import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { toast } from 'sonner';
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/shared/SEO';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id.replace('first-name', 'firstName').replace('last-name', 'lastName')]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send the email using Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('direct-email', {
        body: {
          to: 'mayur@roundone.ai',
          templateAlias: 'contact-form',
          templateModel: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            subject: formData.subject,
            message: formData.message
          },
          from: 'hello@roundone.ai',
          replyTo: formData.email
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      // Reset form data after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });

      toast.success('Your message has been sent successfully!');
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast.error(`Failed to send your message. Please try again later. ${error instanceof Error ? error.message : ''}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create contact page structured data
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact RoundOne AI",
    "description": "Get in touch with the RoundOne AI team for sales, support, or general inquiries.",
    "url": "https://roundone.ai/contact"
  };

  // Create organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "RoundOne AI",
    "url": "https://roundone.ai",
    "logo": "https://roundone.ai/images/dark-logo.png",
    "sameAs": [
      "https://www.linkedin.com/company/roundoneai",
      "https://x.com/RoundOneAi",
      "https://www.youtube.com/@RoundOneAI"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "hello@roundone.ai"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="Contact Us"
        description="Have questions about RoundOne AI? Get in touch with our team for support, sales inquiries, or general information."
        url="/contact"
        schema={[contactSchema, organizationSchema]}
      />
      
      {/* Use the shared Header component */}
      <Header />
      
      {/* Main content */}
      <main className="flex-1">
        {/* Hero section - changed to dark purple background */}
        <section className="bg-roundone-dark-purple py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-6 text-white">Contact Us</h1>
              <p className="text-lg text-roundone-white/80 mb-8">
                Have questions about RoundOne AI? We're here to help.
                Reach out to our team for support, sales inquiries, or general information.
              </p>
            </div>
          </div>
        </section>
        
        {/* Contact details */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto">
              {/* Social Media Links */}
              <div className="mb-10 text-center">
                <h2 className="text-2xl font-bold mb-6 text-center">Connect With Us</h2>
                <div className="flex justify-center space-x-8">
                  <a href="https://www.linkedin.com/company/roundoneai" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-roundone-purple/10 flex items-center justify-center group-hover:bg-roundone-purple/20 transition-colors">
                      <svg className="h-6 w-6 text-roundone-purple" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="mt-2 text-sm font-medium text-roundone-purple">LinkedIn</span>
                  </a>
                  <a href="https://x.com/RoundOneAi" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-roundone-purple/10 flex items-center justify-center group-hover:bg-roundone-purple/20 transition-colors">
                      <svg className="h-6 w-6 text-roundone-purple" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                      </svg>
                    </div>
                    <span className="mt-2 text-sm font-medium text-roundone-purple">X / Twitter</span>
                  </a>
                  <a href="https://www.youtube.com/@RoundOneAI" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-roundone-purple/10 flex items-center justify-center group-hover:bg-roundone-purple/20 transition-colors">
                      <svg className="h-6 w-6 text-roundone-purple" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="mt-2 text-sm font-medium text-roundone-purple">YouTube</span>
                  </a>
                </div>
              </div>
              
              {/* Contact form */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-center">Send us a message</h2>
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                        First name
                      </label>
                      <input
                        type="text"
                        id="first-name"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                        Last name
                      </label>
                      <input
                        type="text"
                        id="last-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                    />
                  </div>
                  <div>
                    <Button 
                      type="submit"
                      className="gradient-button w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Add FrequentlyAskedQuestions component */}
        <FrequentlyAskedQuestions />
        
        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default Contact;
